import { useEffect, useState, useCallback } from "react";
import { api } from "../api/client.js";
import { AuthScreen } from "./AuthScreen.jsx";

// Įsimenamas paskutinis pasirinktas rodinys (verslo/asmeninė), kad puslapio
// perkrovimas neišmestų dvigubos paskyros vartotojo visada atgal į "Verslo".
const ACTIVE_VIEW_KEY = "sannamai_active_view";

function readStoredView() {
  try { return localStorage.getItem(ACTIVE_VIEW_KEY); } catch { return null; }
}
function storeView(view) {
  try {
    if (view) localStorage.setItem(ACTIVE_VIEW_KEY, view);
    else localStorage.removeItem(ACTIVE_VIEW_KEY);
  } catch {}
}

export function AuthGate({ children }) {
  const [status, setStatus] = useState("loading"); // loading | authenticated | unauthenticated
  const [businessUser, setBusinessUser] = useState(null);
  const [casualUser, setCasualUser] = useState(null);
  const [activeView, setActiveViewState] = useState(null); // 'business' | 'casual'

  const setActiveView = useCallback((view) => {
    setActiveViewState(view);
    storeView(view);
  }, []);

  const checkSession = useCallback(async () => {
    setStatus("loading");
    const [businessResult, casualResult] = await Promise.allSettled([
      api.get("/api/business/me"),
      api.get("/api/casual/me"),
    ]);

    const business = businessResult.status === "fulfilled" ? businessResult.value : null;
    const casual = casualResult.status === "fulfilled" ? casualResult.value : null;
    setBusinessUser(business);
    setCasualUser(casual);

    if (business || casual) {
      const preferred = readStoredView();
      setActiveViewState((prev) => {
        const wanted = prev || preferred;
        if (wanted === "business" && business) return "business";
        if (wanted === "casual" && casual) return "casual";
        return business ? "business" : "casual";
      });
      setStatus("authenticated");
    } else {
      setActiveViewState(null);
      setStatus("unauthenticated");
    }
  }, []);

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  async function handleLogout() {
    // Log out of both — harmless no-op (401, caught) for whichever session
    // this account doesn't actually have.
    await Promise.allSettled([api.post("/api/business/auth/logout"), api.post("/api/casual/auth/logout")]);
    setBusinessUser(null);
    setCasualUser(null);
    setActiveView(null);
    setStatus("unauthenticated");
  }

  if (status === "loading") {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          flexDirection: "column",
          gap: 12,
          background: "#F5F6FA",
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            border: "4px solid #e5e7eb",
            borderTopColor: "#735DFF",
            borderRadius: "50%",
            animation: "spin 0.8s linear infinite",
          }}
        />
        <div style={{ fontSize: 14, color: "#383853", fontWeight: 600 }}>Kraunama programa...</div>
      </div>
    );
  }

  if (status === "unauthenticated") {
    return <AuthScreen onAuthenticated={checkSession} />;
  }

  return children({
    businessUser,
    casualUser,
    activeView,
    setActiveView,
    canSwitchViews: Boolean(businessUser && casualUser),
    onLogout: handleLogout,
  });
}
